import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import { Layout, ProgressBar, TextStyle } from "@shopify/polaris";
import { useApolloClient } from "@apollo/react-hooks";

import Moment from "moment";

// import hoc
import { withErrorBoundary, withFeature } from "lib/hoc";

import constant from "lib/constant/constant";
import { baseHelper } from "lib/helpers";
import { useBatchExport } from "lib/hooks";
import { PrivateContext } from "lib/context";
import { Banner } from "lib/components";

import { GET_ORDER_LAKE_LINE } from "app/orderLake/apollo/queries";

import orderLakeHelper from "app/orderLake/modules/orderLakeHelper";

const ExportOrder = () => {
  const data = useContext(PrivateContext);
  const { cms, currentUser } = data;
  const client = useApolloClient();
  const hasStartedRef = useRef(false);
  const isVendor = baseHelper.isVendor(currentUser);

  const [banner, setBanner] = useState({
    title: "",
    status: "",
    isOpen: false,
  });

  const showErrorBanner = useCallback((title) => {
    setBanner({ title, status: constant.CRITICAL, isOpen: true });
    setTimeout(() => window.close(), 2000);
  }, []);

  const fetchPage = useCallback(
    (page, filters) =>
      client
        .query({
          query: GET_ORDER_LAKE_LINE,
          variables: { input: { ...filters, page } },
          fetchPolicy: "network-only",
        })
        .then((res) => {
          const response = res.data.getOrderLakeLine;
          if (response.status !== constant.OK) {
            throw new Error(response.error || cms("common.message.error.noOrder"));
          }
          return { rows: response.data || [], hasMore: response.hasMore, totalCount: response.totalCount };
        }),
    [client, cms]
  );

  const mapRows = useCallback((rows) => orderLakeHelper.orderCSVData(rows, true, false, isVendor), [isVendor]);

  const getFileName = useCallback(() => {
    const date = Moment(new Date(), "DD-MM-YYYY").format("LL");
    return `order ${date}.csv`;
  }, []);

  const onError = useCallback((err) => showErrorBanner(err.message || cms("common.message.error.noOrder")), [
    showErrorBanner,
    cms,
  ]);

  const { status: exportStatus, fetchedCount, totalCount, progress, start } = useBatchExport({
    fetchPage,
    mapRows,
    getFileName,
    onError,
  });

  useEffect(() => {
    if (exportStatus === "empty") {
      showErrorBanner(cms("common.message.error.noOrder"));
    }
    if (exportStatus === "done") {
      setTimeout(() => window.close(), 500);
    }
  }, [exportStatus, showErrorBanner, cms]);

  useEffect(() => {
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;

    const { status, storeStatus, vendor, accountingStatus } = baseHelper.queryParamsFromLocation(data);
    const filters = {};
    if (status && status.length) {
      filters.status = status.split(",");
    }
    if (storeStatus && storeStatus.length) {
      filters.storeStatus = storeStatus.split(",");
    }
    if (vendor && vendor.length) {
      filters.vendor = vendor.split(",");
    }
    if (accountingStatus && accountingStatus.length) {
      filters.accountingStatus = accountingStatus.split(",");
    }
    start(filters);
  }, [data, start]);

  return (
    <Layout>
      <Layout.Section>
        {exportStatus === "loading" && (
          <div className="fullPageProgress">
            <ProgressBar progress={progress} size="medium" />
            <br />
            <TextStyle variation="subdued">
              {totalCount ? `Exporting ${fetchedCount} of ${totalCount} records...` : "Preparing export..."}
            </TextStyle>
          </div>
        )}
        {banner.isOpen && (
          <Banner
            title={banner.title}
            status={banner.status}
            isOpen={banner.isOpen}
            onDismiss={() => setBanner({ isOpen: false, status: "", title: "" })}
          />
        )}
      </Layout.Section>
    </Layout>
  );
};

export default withFeature(withErrorBoundary(ExportOrder), { feature: constant.EXPORT_ORDER });
